import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { dashboardApi } from '@/utils/api';
import type { DashboardKPIs, CategoryCompliance, QuarterlyReadiness, ApiResponse } from '@/types';

interface DashboardState {
  executive: {
    kpis: DashboardKPIs | null;
    categoryCompliance: CategoryCompliance | null;
    quarterlyReadiness: QuarterlyReadiness[] | null;
    investmentByIS: any[] | null;
    dependenciesAtRisk: any[] | null;
    epicDeliveryTrend: any[] | null;
    isLoading: boolean;
    error: string | null;
  };
  isOwner: {
    capacitySummary: any | null;
    featureReadiness: any[] | null;
    epics: any[] | null;
    contributingPrograms: any[] | null;
    okrCount: number;
    isLoading: boolean;
    error: string | null;
  };
  programLead: {
    epicAccountability: any[] | null;
    capacityUtilization: any | null;
    categoryCompliance: CategoryCompliance | null;
    crossProgramCoordination: any[] | null;
    allocatedEpicsCount: number;  // EPICs where program has allocations
    supportedISCount: number;     // Unique ISs the program supports via allocations
    isLoading: boolean;
    error: string | null;
  };
  capacity: {
    utilization: any | null;
    programCapacity: any | null;
    teamCapacity: any | null;
    isLoading: boolean;
    error: string | null;
  };
}

const transformExecutiveDashboardData = (data: any) => {
  // KPIs
  const totalInvestmentDollars = data?.investmentSummary?.totalInvestment?.dollarAmount ?? 0;
  const totalInvestmentTeamWeeks = data?.investmentSummary?.totalInvestment?.teamWeeks ?? 0;

  const q1 = data?.quarterlyReadinessTrend?.find((q: any) => q.quarter === 1) 
    || data?.quarterlyReadinessTrend?.[0];
  const q1Total = q1?.totalFeatures ?? 0;
  const q1Ready = q1?.shovelReady ?? 0;
  const q1ReadyPct = q1?.readinessPercentage ?? (q1Total > 0 ? (q1Ready / q1Total) * 100 : 0);
  const q1Status = q1ReadyPct >= 70 ? 'on_track' : q1ReadyPct >= 50 ? 'at_risk' : 'off_track';

  const epics = data?.epicDeliveryTrend || [];
  const onTrackEpics = epics.filter((e: any) => e.deliveryStatus === 'ON_TRACK').length;
  const atRiskEpics = epics.filter((e: any) => e.deliveryStatus === 'AT_RISK').length;
  const offTrackEpics = epics.filter((e: any) => e.deliveryStatus === 'OFF_TRACK').length;
  const deliveredEpics = epics.filter((e: any) => e.status === 'DELIVERED').length;
  const activeEpicsTotal = epics.length;
  const deliveryRate = activeEpicsTotal > 0 ? (onTrackEpics / activeEpicsTotal) * 100 : 0;

  const dependencyList = data?.dependenciesAtRisk || [];
  const dependenciesAtRisk = {
    total: dependencyList.length,
    critical: dependencyList.filter((d: any) => d.severity === 'CRITICAL').length,
    high: dependencyList.filter((d: any) => d.severity === 'HIGH').length,
  };

  const kpis: DashboardKPIs = {
    totalInvestment: {
      dollars: totalInvestmentDollars,
      teamWeeks: totalInvestmentTeamWeeks,
      change: 0, // Placeholder until we have period-over-period data
    },
    q1Readiness: {
      percentage: Number(q1ReadyPct.toFixed(1)),
      readyFeatures: q1Ready,
      totalFeatures: q1Total,
      status: q1Status,
    },
    activeEpics: {
      total: activeEpicsTotal,
      onTrack: onTrackEpics,
      atRisk: atRiskEpics,
      offTrack: offTrackEpics,
    },
    dependenciesAtRisk,
  };

  // Category compliance
  const categoryComplianceArray = data?.categoryCompliance || [];
  const complianceMap = categoryComplianceArray.reduce((acc: any, cat: any) => {
    const key = cat.category?.toLowerCase?.();
    if (key) {
      acc[key] = {
        actual: cat.actualPercentage ?? 0,
        target: cat.targetPercentage ?? 0,
        status: cat.status === 'COMPLIANT' ? 'compliant' : 'non_compliant',
        trend: 'stable' as const,
      };
    }
    return acc;
  }, {});

  const categoryCompliance: CategoryCompliance | null = complianceMap.business && complianceMap.tech && complianceMap.support
    ? {
        business: complianceMap.business,
        tech: complianceMap.tech,
        support: complianceMap.support,
        overallCompliance: (['business', 'tech', 'support'] as const).reduce((score, key) => 
          complianceMap[key]?.status === 'compliant' ? score + (100 / 3) : score, 0),
        complianceScore: (['business', 'tech', 'support'] as const).reduce((score, key) => 
          complianceMap[key]?.status === 'compliant' ? score + 1 : score, 0),
      }
    : null;

  // Quarterly readiness
  const quarterlyReadiness: QuarterlyReadiness[] | null = Array.isArray(data?.quarterlyReadinessTrend)
    ? data.quarterlyReadinessTrend.map((q: any) => {
        const total = q.totalFeatures ?? 0;
        const ready = q.shovelReady ?? 0;
        const inRefinement = q.inRefinement ?? 0;
        const notReady = q.notReady ?? 0;
        return {
          quarter: q.quarter,
          readyPercentage: total > 0 ? (ready / total) * 100 : 0,
          inRefinementPercentage: total > 0 ? (inRefinement / total) * 100 : 0,
          notReadyPercentage: total > 0 ? (notReady / total) * 100 : 0,
          totalFeatures: total,
        };
      })
    : null;

  // Investment by IS
  const investmentByISRaw = data?.investmentByIS || [];
  const totalInvestmentAll = investmentByISRaw.reduce((sum: number, item: any) => sum + (item.dollarAmount ?? 0), 0);
  const investmentByIS = investmentByISRaw.map((item: any) => ({
    investibleStructureId: item.isId,
    investibleStructureName: item.isName,
    category: item.category,
    totalInvestment: item.dollarAmount ?? 0,
    teamWeeks: item.teamWeeks ?? 0,
    epicCount: item.epicCount ?? 0,
    percentage: totalInvestmentAll > 0 ? ((item.dollarAmount ?? 0) / totalInvestmentAll) * 100 : 0,
  }));

  // Dependencies
  const dependencies = dependencyList.map((dep: any) => ({
    id: dep.id,
    title: dep.name,
    description: `${dep.sourceName || dep.sourceType} -> ${dep.targetName || dep.targetType}`,
    severity: dep.severity,
    status: dep.status,
    dependentEntity: {
      type: dep.sourceType,
      name: dep.sourceName,
      id: dep.sourceId,
    },
    blockedEntity: {
      type: dep.targetType,
      name: dep.targetName,
      id: dep.targetId,
    },
    dueDate: dep.expectedDate,
    createdAt: dep.expectedDate || new Date().toISOString(),
  }));

  // Epic delivery trend (aggregated to current period for UI expectations)
  const deliveryTrend = [
    {
      period: 'Current',
      onTrack: onTrackEpics,
      atRisk: atRiskEpics,
      offTrack: offTrackEpics,
      total: activeEpicsTotal,
      deliveredCount: deliveredEpics,
      deliveryRate,
    },
  ];

  return {
    kpis,
    categoryCompliance,
    quarterlyReadiness,
    investmentByIS,
    dependenciesAtRisk: dependencies,
    epicDeliveryTrend: deliveryTrend,
  };
};

const initialState: DashboardState = {
  executive: {
    kpis: null,
    categoryCompliance: null,
    quarterlyReadiness: null,
    investmentByIS: null,
    dependenciesAtRisk: null,
    epicDeliveryTrend: null,
    isLoading: false,
    error: null,
  },
  isOwner: {
    capacitySummary: null,
    featureReadiness: null,
    epics: null,
    contributingPrograms: null,
    okrCount: 0,
    isLoading: false,
    error: null,
  },
  programLead: {
    epicAccountability: null,
    capacityUtilization: null,
    categoryCompliance: null,
    crossProgramCoordination: null,
    allocatedEpicsCount: 0,
    supportedISCount: 0,
    isLoading: false,
    error: null,
  },
  capacity: {
    utilization: null,
    programCapacity: null,
    teamCapacity: null,
    isLoading: false,
    error: null,
  },
};

// Executive Dashboard Thunks
export const fetchExecutiveDashboard = createAsyncThunk(
  'dashboard/fetchExecutiveDashboard',
  async (params?: { planningPeriodId?: string; scenarioId?: string }, { rejectWithValue }) => {
    const response = await dashboardApi.getExecutiveDashboard(params);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch executive dashboard');
    }
    return response.data;
  }
);

// IS Owner Dashboard Thunks
export const fetchISOwnerDashboard = createAsyncThunk(
  'dashboard/fetchISOwnerDashboard',
  async (params?: { 
    investibleStructureId?: string; 
    planningPeriodId?: string; 
    scenarioId?: string 
  }, { rejectWithValue }) => {
    const response = await dashboardApi.getISOwnerDashboard(params);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch IS owner dashboard');
    }
    return response.data;
  }
);

// Program Lead Dashboard Thunks
export const fetchProgramLeadDashboard = createAsyncThunk(
  'dashboard/fetchProgramLeadDashboard',
  async (params?: { 
    programId?: string; 
    planningPeriodId?: string; 
    scenarioId?: string 
  }, { rejectWithValue }) => {
    const response = await dashboardApi.getProgramLeadDashboard(params);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch program lead dashboard');
    }
    return response.data;
  }
);

// Capacity Utilization Thunks
export const fetchCapacityUtilization = createAsyncThunk(
  'dashboard/fetchCapacityUtilization',
  async (params?: { programId?: string; quarter?: number }, { rejectWithValue }) => {
    try {
      const response = await dashboardApi.getCapacityUtilization(params);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch capacity utilization');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch capacity utilization');
    }
  }
);

export const fetchProgramCapacity = createAsyncThunk(
  'dashboard/fetchProgramCapacity',
  async (programId: string, { rejectWithValue }) => {
    try {
      const response = await dashboardApi.getProgramCapacity(programId);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch program capacity');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch program capacity');
    }
  }
);

export const fetchTeamCapacity = createAsyncThunk(
  'dashboard/fetchTeamCapacity',
  async (teamId: string, { rejectWithValue }) => {
    try {
      const response = await dashboardApi.getTeamCapacity(teamId);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch team capacity');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch team capacity');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearExecutiveError: (state) => {
      state.executive.error = null;
    },
    clearISOwnerError: (state) => {
      state.isOwner.error = null;
    },
    clearProgramLeadError: (state) => {
      state.programLead.error = null;
    },
    clearCapacityError: (state) => {
      state.capacity.error = null;
    },
    clearAllDashboardData: (state) => {
      state.executive = { ...initialState.executive };
      state.isOwner = { ...initialState.isOwner };
      state.programLead = { ...initialState.programLead };
      state.capacity = { ...initialState.capacity };
    },
  },
  extraReducers: (builder) => {
    builder
      // Executive Dashboard
      .addCase(fetchExecutiveDashboard.pending, (state) => {
        state.executive.isLoading = true;
        state.executive.error = null;
      })
      .addCase(fetchExecutiveDashboard.fulfilled, (state, action) => {
        state.executive.isLoading = false;
        const transformed = transformExecutiveDashboardData(action.payload);
        state.executive.kpis = transformed.kpis;
        state.executive.categoryCompliance = transformed.categoryCompliance;
        state.executive.quarterlyReadiness = transformed.quarterlyReadiness;
        state.executive.investmentByIS = transformed.investmentByIS;
        state.executive.dependenciesAtRisk = transformed.dependenciesAtRisk;
        state.executive.epicDeliveryTrend = transformed.epicDeliveryTrend;
      })
      .addCase(fetchExecutiveDashboard.rejected, (state, action) => {
        state.executive.isLoading = false;
        state.executive.error = action.payload as string;
      })
      // IS Owner Dashboard
      .addCase(fetchISOwnerDashboard.pending, (state) => {
        state.isOwner.isLoading = true;
        state.isOwner.error = null;
      })
      .addCase(fetchISOwnerDashboard.fulfilled, (state, action) => {
        state.isOwner.isLoading = false;
        state.isOwner.capacitySummary = action.payload.capacitySummary;
        state.isOwner.featureReadiness = action.payload.featureReadiness;
        state.isOwner.epics = action.payload.epics;
        state.isOwner.contributingPrograms = action.payload.contributingPrograms;
        state.isOwner.okrCount = action.payload.okrCount || 0;
      })
      .addCase(fetchISOwnerDashboard.rejected, (state, action) => {
        state.isOwner.isLoading = false;
        state.isOwner.error = action.payload as string;
      })
      // Program Lead Dashboard
      .addCase(fetchProgramLeadDashboard.pending, (state) => {
        state.programLead.isLoading = true;
        state.programLead.error = null;
      })
      .addCase(fetchProgramLeadDashboard.fulfilled, (state, action) => {
        state.programLead.isLoading = false;
        
        // Transform epic accountability data to match component interface
        const payload = action.payload as any;
        const epicAccountability = payload.epicAccountability;
        const transformedEpicAccountability = {
          primaryEpics: epicAccountability.primary.map((epic: any) => ({
            id: epic.epicId,
            name: epic.epicName,
            status: epic.status,
            role: epic.role.toLowerCase(),
            requestedTeamWeeks: epic.requestedTeamWeeks,
            allocatedTeamWeeks: epic.allocatedTeamWeeks,
            utilizationPercentage: epic.utilizationPercentage,
            investibleStructure: {
              name: epic.isName,
              category: 'BUSINESS', // Default category, could be enhanced
            },
          })),
          contributingEpics: epicAccountability.contributing.map((epic: any) => ({
            id: epic.epicId,
            name: epic.epicName,
            status: epic.status,
            role: epic.role.toLowerCase(),
            requestedTeamWeeks: epic.requestedTeamWeeks,
            allocatedTeamWeeks: epic.allocatedTeamWeeks,
            utilizationPercentage: epic.utilizationPercentage,
            investibleStructure: {
              name: epic.isName,
              category: 'BUSINESS', // Default category, could be enhanced
            },
          })),
          totalPrimaryCapacity: epicAccountability.primary.reduce((sum: number, epic: any) => sum + epic.allocatedTeamWeeks, 0),
          totalContributingCapacity: epicAccountability.contributing.reduce((sum: number, epic: any) => sum + epic.allocatedTeamWeeks, 0),
        };
        
        state.programLead.epicAccountability = transformedEpicAccountability as any;
        
        // Transform capacity utilization data
        const capacityData = payload.capacityAllocationUtilization;
        const transformedCapacityUtilization = {
          totalCapacity: capacityData.totalCapacity,
          allocatedCapacity: capacityData.allocatedCapacity,
          availableCapacity: capacityData.availableCapacity,
          utilizationPercentage: capacityData.utilizationPercentage,
          teamCount: capacityData.teamBreakdown.length,
          teams: capacityData.teamBreakdown.map((team: any) => ({
            teamId: team.teamId,
            teamName: team.teamName,
            memberCount: team.memberCount,
            totalCapacity: team.capacity,
            allocatedCapacity: team.allocated,
            utilizationPercentage: team.utilization,
            overAllocation: Math.max(0, team.allocated - team.capacity),
          })),
          quarterlyTrend: [], // Could be enhanced with actual quarterly data
        };
        
        state.programLead.capacityUtilization = transformedCapacityUtilization;
        
        // Transform category compliance data
        const categoryData = payload.categoryCompliance[0]; // Assuming single category for now
        const transformedCategoryCompliance = {
          business: {
            actual: categoryData?.category === 'BUSINESS' ? categoryData.actual : 75,
            target: categoryData?.category === 'BUSINESS' ? categoryData.target : 75,
            status: (categoryData?.status === 'COMPLIANT' ? 'compliant' : 'non_compliant') as 'compliant' | 'non_compliant',
            trend: 'stable' as const,
          },
          tech: {
            actual: categoryData?.category === 'TECH' ? categoryData.actual : 15,
            target: categoryData?.category === 'TECH' ? categoryData.target : 15,
            status: (categoryData?.status === 'COMPLIANT' ? 'compliant' : 'non_compliant') as 'compliant' | 'non_compliant',
            trend: 'stable' as const,
          },
          support: {
            actual: categoryData?.category === 'SUPPORT' ? categoryData.actual : 10,
            target: categoryData?.category === 'SUPPORT' ? categoryData.target : 10,
            status: (categoryData?.status === 'COMPLIANT' ? 'compliant' : 'non_compliant') as 'compliant' | 'non_compliant',
            trend: 'stable' as const,
          },
          overallCompliance: categoryData ? (categoryData.status === 'COMPLIANT' ? 100 : 60) : 80,
          complianceScore: categoryData ? (categoryData.status === 'COMPLIANT' ? 3 : 1) : 2,
        };
        
        state.programLead.categoryCompliance = transformedCategoryCompliance as any;
        
        // Transform cross-program coordination data
        const coordinationData = payload.crossProgramCoordination;
        const transformedCoordination = {
          coordinationPoints: coordinationData.map((coord: any) => ({
            id: coord.epicId,
            type: 'shared_epic',
            title: coord.epicName,
            description: `Cross-program coordination for ${coord.isName}`,
            relatedProgram: {
              id: coord.program,
              name: coord.program,
            },
            severity: coord.coordinationComplexity.toLowerCase(),
            status: 'open',
          })),
          totalCoordinationPoints: coordinationData.length,
          criticalPoints: coordinationData.filter((coord: any) => coord.coordinationComplexity === 'HIGH').length,
          resolvedPoints: 0, // Could be enhanced with actual resolved data
          programsInvolved: coordinationData.flatMap((coord: any) => 
            coord.contributingPrograms.map((prog: any) => ({
              programId: prog.programId,
              programName: prog.programName,
              coordinationCount: 1,
            }))
          ),
        };
        
        state.programLead.crossProgramCoordination = transformedCoordination as any;

        // Extract new KPIs for allocation-based metrics
        const kpis = payload.kpis;
        state.programLead.allocatedEpicsCount = kpis?.allocatedEpicsCount || 0;
        state.programLead.supportedISCount = kpis?.supportedISCount || 0;
      })
      .addCase(fetchProgramLeadDashboard.rejected, (state, action) => {
        state.programLead.isLoading = false;
        state.programLead.error = action.payload as string;
      })
      // Capacity Utilization
      .addCase(fetchCapacityUtilization.pending, (state) => {
        state.capacity.isLoading = true;
        state.capacity.error = null;
      })
      .addCase(fetchCapacityUtilization.fulfilled, (state, action) => {
        state.capacity.isLoading = false;
        state.capacity.utilization = action.payload;
      })
      .addCase(fetchCapacityUtilization.rejected, (state, action) => {
        state.capacity.isLoading = false;
        state.capacity.error = action.payload as string;
      })
      // Program Capacity
      .addCase(fetchProgramCapacity.pending, (state) => {
        state.capacity.isLoading = true;
        state.capacity.error = null;
      })
      .addCase(fetchProgramCapacity.fulfilled, (state, action) => {
        state.capacity.isLoading = false;
        state.capacity.programCapacity = action.payload;
      })
      .addCase(fetchProgramCapacity.rejected, (state, action) => {
        state.capacity.isLoading = false;
        state.capacity.error = action.payload as string;
      })
      // Team Capacity
      .addCase(fetchTeamCapacity.pending, (state) => {
        state.capacity.isLoading = true;
        state.capacity.error = null;
      })
      .addCase(fetchTeamCapacity.fulfilled, (state, action) => {
        state.capacity.isLoading = false;
        state.capacity.teamCapacity = action.payload;
      })
      .addCase(fetchTeamCapacity.rejected, (state, action) => {
        state.capacity.isLoading = false;
        state.capacity.error = action.payload as string;
      });
  },
});

export const { 
  clearExecutiveError, 
  clearISOwnerError, 
  clearProgramLeadError, 
  clearCapacityError,
  clearAllDashboardData 
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
